import React from 'react';
import { motion } from 'framer-motion';
import { SectionLabel, SectionHeading, PrimaryButton } from './ui/UI';
import { ArrowRight } from 'lucide-react';
const includes = ['Top realistic attack paths', 'Evidence behind each path', 'Plain-English explanation', 'Business impact', 'Remediation steps', 'Verification plan', 'Shareable summary', 'Technical appendix'];
const flow = ['Connect your environment', 'Run an attack-path report', 'See the few paths that matter', 'Fix the highest-impact risks', 'Verify closure', 'Export proof'];
export function FirstReport() {
  return <section className="relative w-full py-32 border-t border-white/5 overflow-hidden" data-id="element-400">
      <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-401">
        <SectionLabel number="08" label="First Product" className="mb-8" data-id="element-402" />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12" data-id="element-403">
          {/* Left: Headline & Flow */}
          <div className="lg:col-span-6" data-id="element-404">
            <SectionHeading size="xl" className="mb-8" data-id="element-405">
              Get your first
              <br data-id="element-406" />
              attack-path report.
            </SectionHeading>
            <p className="text-lg text-white/60 leading-relaxed mb-10" data-id="element-407">
              The first Periscan experience is simple. No 500-page pentest. No
              raw scanner dump. No guessing what to fix first.
            </p>

            {/* Flow */}
            <div className="space-y-0 mb-10" data-id="element-408">
              {flow.map((f, i) => <motion.div key={i} initial={{
              opacity: 0,
              x: -10
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.4,
              delay: i * 0.06
            }} className="flex items-center gap-4 py-3 border-b border-white/5 last:border-0" data-id="element-409">
                  <span className="font-mono text-xs text-[#1a8fd4]/60 w-8" data-id="element-410">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <span className="text-base text-white" data-id="element-411">{f}</span>
                </motion.div>)}
            </div>

            <PrimaryButton href="#early-access" size="lg" data-id="element-412">
              Get Your First Attack-Path Report
            </PrimaryButton>

            <p className="mt-6 text-sm text-white/40" data-id="element-413">
              Early access reports may be guided during the first beta phase
              while the self-serve product is finalized.
            </p>
          </div>

          {/* Right: Mock report */}
          <div className="lg:col-span-6" data-id="element-414">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="relative rounded-2xl bg-gradient-to-br from-[#0a0d12] to-[#050608] border border-white/10 overflow-hidden shadow-2xl" data-id="element-415">
              {/* Report header */}
              <div className="px-6 py-4 border-b border-white/5 flex items-center justify-between bg-white/[0.02]" data-id="element-416">
                <div className="flex items-center gap-3" data-id="element-417">
                  <div className="flex gap-1.5" data-id="element-418">
                    <div className="w-2.5 h-2.5 rounded-full bg-white/10" data-id="element-419" />
                    <div className="w-2.5 h-2.5 rounded-full bg-white/10" data-id="element-420" />
                    <div className="w-2.5 h-2.5 rounded-full bg-white/10" data-id="element-421" />
                  </div>
                  <span className="font-mono text-xs text-white/50" data-id="element-422">
                    attack-path-report.pdf
                  </span>
                </div>
                <span className="font-mono text-[10px] uppercase tracking-widest text-[#1a8fd4]" data-id="element-423">
                  Confidential
                </span>
              </div>

              {/* Report body */}
              <div className="p-8 space-y-6" data-id="element-424">
                <div data-id="element-425">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-white/40 mb-2" data-id="element-426">
                    Executive Summary
                  </p>
                  <p className="text-white text-lg font-semibold" data-id="element-427">
                    3 critical paths require remediation
                  </p>
                </div>

                <div className="space-y-3" data-id="element-428">
                  {includes.slice(0, 5).map((item, i) => <motion.div key={i} initial={{
                  opacity: 0
                }} whileInView={{
                  opacity: 1
                }} viewport={{
                  once: true
                }} transition={{
                  duration: 0.4,
                  delay: 0.2 + i * 0.1
                }} className="flex items-center justify-between py-2.5 border-b border-white/5 last:border-0" data-id="element-429">
                      <div className="flex items-center gap-3" data-id="element-430">
                        <div className="w-1 h-1 rounded-full bg-[#1a8fd4]" data-id="element-431" />
                        <span className="text-sm text-white/70" data-id="element-432">{item}</span>
                      </div>
                      <span className="font-mono text-[10px] text-white/30" data-id="element-433">
                        ✓
                      </span>
                    </motion.div>)}
                </div>

                <div className="pt-4 border-t border-white/5" data-id="element-434">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-white/40 mb-3" data-id="element-435">
                    Includes
                  </p>
                  <div className="flex flex-wrap gap-1.5" data-id="element-436">
                    {includes.map((item, i) => <span key={i} className="px-2 py-1 text-[11px] text-white/50 bg-white/[0.03] border border-white/5 rounded-md" data-id="element-437">
                        {item}
                      </span>)}
                  </div>
                </div>
              </div>

              {/* Scanning effect */}
              <div className="absolute inset-x-0 top-0 bottom-0 overflow-hidden pointer-events-none" data-id="element-438">
                <div className="absolute inset-y-0 w-32 bg-gradient-to-r from-transparent via-[#1a8fd4]/10 to-transparent animate-scan" data-id="element-439" />
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>;
}